import React, { useState, useEffect } from 'react';
import '../../styles/modal.css';
import '../../styles/ai-test-case-modal.css';

/**
 * Modal for generating test cases using AI based on requirements
 */
const AITestCaseGenerationModal = ({ isOpen, onClose, requirement, module, projectId, onTestCasesGenerated }) => {
  const [generating, setGenerating] = useState(false);
  const [generatedTestCases, setGeneratedTestCases] = useState([]);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  // Reset state when modal opens with new requirement
  useEffect(() => {
    if (isOpen) {
      setGenerating(false);
      setGeneratedTestCases([]);
      setProgress(0);
      setError(null);
      // Start generation automatically when modal opens
      handleGenerateTestCases();
    }
  }, [isOpen, requirement]);

  // Simulate AI test case generation
  const handleGenerateTestCases = () => {
    setGenerating(true);
    setProgress(0);
    
    // Simulate progress updates
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + Math.floor(Math.random() * 10);
        return newProgress >= 100 ? 100 : newProgress;
      });
    }, 300);
    
    // Simulate API call to generate test cases
    setTimeout(() => {
      clearInterval(progressInterval);
      setProgress(100);
      
      // Generate mock test cases based on the requirement
      const mockTestCases = generateMockTestCases(requirement, module);
      setGeneratedTestCases(mockTestCases);
      setGenerating(false);
    }, 3000);
  };

  // Generate mock test cases based on the requirement and module
  const generateMockTestCases = (requirement, module) => {
    let testCases = [];
    
    // Authentication module test cases
    if (module === 'Authentication' && requirement.includes('reset')) {
      testCases = [
        {
          id: Math.floor(Math.random() * 1000),
          title: 'Request password reset with valid email',
          steps: '1. Navigate to login page\n2. Click "Forgot Password" link\n3. Enter valid email address\n4. Click "Reset Password" button',
          expected: 'Password reset email is sent to the user',
          priority: 'High',
          status: 'New'
        },
        {
          id: Math.floor(Math.random() * 1000),
          title: 'Request password reset with invalid email',
          steps: '1. Navigate to login page\n2. Click "Forgot Password" link\n3. Enter invalid email address\n4. Click "Reset Password" button',
          expected: 'Error message is displayed indicating invalid email',
          priority: 'Medium',
          status: 'New'
        },
        {
          id: Math.floor(Math.random() * 1000),
          title: 'Reset password with valid token',
          steps: '1. Open password reset link from email\n2. Enter new password\n3. Confirm new password\n4. Click "Save" button',
          expected: 'Password is updated and user is redirected to login page',
          priority: 'High',
          status: 'New'
        }
      ];
    } 
    // Product Catalog module test cases
    else if (module === 'Product Catalog' && requirement.includes('filtering')) {
      testCases = [
        {
          id: Math.floor(Math.random() * 1000),
          title: 'Filter products by single attribute',
          steps: '1. Navigate to product catalog\n2. Select a filter attribute (e.g., color)\n3. Select a filter value\n4. Apply filter',
          expected: 'Products matching the selected filter are displayed',
          priority: 'High',
          status: 'New'
        },
        {
          id: Math.floor(Math.random() * 1000),
          title: 'Filter products by multiple attributes',
          steps: '1. Navigate to product catalog\n2. Select multiple filter attributes (e.g., color, size, price range)\n3. Apply filters',
          expected: 'Products matching all selected filters are displayed',
          priority: 'High',
          status: 'New'
        },
        {
          id: Math.floor(Math.random() * 1000),
          title: 'Clear all filters',
          steps: '1. Apply multiple filters\n2. Click "Clear All Filters" button',
          expected: 'All filters are removed and all products are displayed',
          priority: 'Medium',
          status: 'New'
        }
      ];
    }
    // Checkout module test cases
    else if (module === 'Checkout' && requirement.includes('discount')) {
      testCases = [
        {
          id: Math.floor(Math.random() * 1000),
          title: 'Apply valid discount code',
          steps: '1. Add items to cart\n2. Proceed to checkout\n3. Enter valid discount code\n4. Click "Apply" button',
          expected: 'Discount is applied and total amount is reduced',
          priority: 'High',
          status: 'New'
        },
        {
          id: Math.floor(Math.random() * 1000),
          title: 'Apply invalid discount code',
          steps: '1. Add items to cart\n2. Proceed to checkout\n3. Enter invalid discount code\n4. Click "Apply" button',
          expected: 'Error message is displayed indicating invalid code',
          priority: 'Medium',
          status: 'New'
        },
        {
          id: Math.floor(Math.random() * 1000),
          title: 'Apply expired discount code',
          steps: '1. Add items to cart\n2. Proceed to checkout\n3. Enter expired discount code\n4. Click "Apply" button',
          expected: 'Error message is displayed indicating expired code',
          priority: 'Medium',
          status: 'New'
        }
      ];
    }
    // Default test cases if no specific module/requirement match
    else {
      testCases = [
        {
          id: Math.floor(Math.random() * 1000),
          title: `Test ${requirement} - Positive scenario`,
          steps: `1. Navigate to ${module}\n2. Perform action related to ${requirement}\n3. Verify results`,
          expected: 'Feature works as expected',
          priority: 'High',
          status: 'New'
        },
        {
          id: Math.floor(Math.random() * 1000),
          title: `Test ${requirement} - Negative scenario`,
          steps: `1. Navigate to ${module}\n2. Perform invalid action related to ${requirement}\n3. Verify error handling`,
          expected: 'Appropriate error message is displayed',
          priority: 'Medium',
          status: 'New'
        }
      ];
    }
    
    // Add project ID to all test cases
    return testCases.map(tc => ({
      ...tc,
      projectId: projectId
    }));
  };

  const handleSaveTestCases = () => {
    // In a real app, this would save the test cases to the database
    onTestCasesGenerated(generatedTestCases);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div data-rt-modal-overlay className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) { const b = e.currentTarget.querySelector('[data-rt-modal-close], .close-btn, .modal-close, button[aria-label="Close"], button[aria-label="close"]'); if (b) b.click(); } }}>
      <div className="modal-container ai-test-case-modal">
        <div className="modal-header">
          <h2>AI Test Case Generation</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          <div className="requirement-info">
            <div className="info-item">
              <span className="info-label">Requirement:</span>
              <span className="info-value">{requirement}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Module:</span>
              <span className="info-value">{module}</span>
            </div>
          </div>
          
          {generating ? (
            <div className="generation-progress">
              <h3>Generating Test Cases...</h3>
              <div className="progress-bar-container">
                <div 
                  className="progress-bar-fill" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <div className="progress-text">{progress}% Complete</div>
              <div className="ai-animation">
                <div className="ai-thinking"></div>
              </div>
            </div>
          ) : (
            <>
              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}
              
              {generatedTestCases.length > 0 && (
                <div className="generated-test-cases">
                  <h3>Generated Test Cases</h3>
                  <div className="test-cases-list">
                    {generatedTestCases.map((testCase, index) => (
                      <div key={index} className="test-case-item">
                        <div className="test-case-header">
                          <h4>{testCase.title}</h4>
                          <span className={`priority-badge ${testCase.priority.toLowerCase()}`}>
                            {testCase.priority}
                          </span>
                        </div>
                        <div className="test-case-details">
                          <div className="detail-section">
                            <h5>Steps:</h5>
                            <pre>{testCase.steps}</pre>
                          </div>
                          <div className="detail-section">
                            <h5>Expected Result:</h5>
                            <pre>{testCase.expected}</pre>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        
        <div className="modal-footer">
          <button 
            className="secondary-button" 
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="primary-button" 
            onClick={handleSaveTestCases}
            disabled={generating || generatedTestCases.length === 0}
          >
            Save Test Cases
          </button>
        </div>
      </div>
    </div>
  );
};

export default AITestCaseGenerationModal;